import { useMemo, useRef, useState } from "react";
import { More, Segment } from "components/field/radarUi";
import { useLabValidation } from "hooks/useRadarLab";
import { fieldTime } from "utils/fieldFormat";
import {
  accuracyRows,
  estimatorInUse,
  finiteNumber,
  formatCm,
  formatMm,
  shareText,
  withinCmShare,
} from "utils/radarLab";
import ErrorChart, { ESTIMATOR_COLORS } from "./LabTruthChart";

export const ACCURACY_DAYS = [
  [1, "1 day"],
  [2, "2 days"],
  [7, "7 days"],
];
// 2 days first: a week of the lab's ruler photos (about 1,500 pairs) is about 770 kB of JSON, and the server works
// every estimate near them out again on each request (about 30 s).
export const DEFAULT_DAYS = 2;
// For the same reason the card asks again only on every 5th of the validation's 2-minute ticks (about every 10 min),
// besides at once for another window or a refresh of the page; the chart's views share one answer.
export const REFRESH_TICKS = 5;
const VIEWS = [
  ["level", "By level", "Error (mm) by the ruler's level (cm)"],
  ["time", "Over time", "Error (mm) over time (SGT)"],
];
const READINGS_FROM = {
  v2: "v2 – the leading edge of the strongest echo",
  v1: "v1 – the peak of the Hann spectrum",
};

const count = (value) => finiteNumber(value) ?? 0;
const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;
const daysText = (days) => (days === 1 ? "day" : `${days} days`);

function lead(data) {
  const photos = data?.photos || {};
  return (
    `Our estimate comes from ${READINGS_FROM[estimatorInUse(data)]}. ` +
    `${plural(count(photos.read), "ruler photo")} read, ${count(photos.paired).toLocaleString()} paired with the ` +
    `node, ${count(photos.low_confidence).toLocaleString()} too doubtful to use.`
  );
}

function AccuracyTable({ rows }) {
  return (
    <div className="field-lab-scroll">
      <table
        className="field-table field-lab-truth field-lab-accuracy-table"
        aria-label="Error against the ruler by estimator"
      >
        <thead>
          <tr>
            <th scope="col">Estimator</th>
            <th scope="col">Photos</th>
            <th scope="col">Median</th>
            <th scope="col">90th percentile</th>
            <th scope="col">Max</th>
            <th scope="col">Within 1 cm</th>
            <th scope="col">Bias</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const errors = row.fit.errors || null;
            return (
              <tr key={row.key} className={row.inUse ? "is-default" : undefined}>
                <th scope="row">
                  <span
                    className="field-radar-key is-dot"
                    style={{ "--key": ESTIMATOR_COLORS[row.key] }}
                    aria-hidden="true"
                  />
                  {row.name}
                  <small>{row.how}</small>
                </th>
                <td>{count(row.fit.n).toLocaleString()}</td>
                <td>{formatMm(errors?.median_cm)}</td>
                <td>{formatMm(errors?.p90_cm)}</td>
                <td>{formatMm(errors?.max_cm)}</td>
                <td>{shareText(withinCmShare(errors))}</td>
                <td>{formatMm(errors?.bias_cm, { signed: true })}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Each estimator's mapping to the ruler: its fitted constant, and how many mm
// its air gap moves per mm of level on a free straight line.
function MappingTable({ rows }) {
  return (
    <div className="field-lab-scroll">
      <table className="field-table field-lab-truth" aria-label="Each estimator's mapping to the ruler">
        <thead>
          <tr>
            <th scope="col">Estimator</th>
            <th scope="col">Constant</th>
            <th scope="col">Air gap per level</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const constant = finiteNumber(row.fit.mapping?.constant_cm);
            const scale = finiteNumber(row.fit.linear?.air_mm_per_level_mm);
            return (
              <tr key={row.key}>
                <th scope="row">{row.name}</th>
                <td>{constant !== null ? formatCm(constant, 2) : "—"}</td>
                <td>{scale !== null ? `${scale.toFixed(3)} mm per mm` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Where the pairs come from: the camera, the server's reader, how a photo is
// paired and what was left out.
function Facts({ data }) {
  const photos = data.photos || {};
  const reader = data.reader || {};
  const unread = count(reader.unread);
  const pairS = finiteNumber(data.pair_s) ?? 30;
  return (
    <dl className="field-lab-facts">
      <div>
        <dt>Camera</dt>
        <dd>{data.camera?.name || data.camera?.mac || "—"}</dd>
      </div>
      <div>
        <dt>Reader</dt>
        <dd>
          Version {reader.version || "—"} · last read{" "}
          {reader.last_read_at ? `${fieldTime(reader.last_read_at)} SGT` : "—"}
          {unread ? ` · ${plural(unread, "photo")} still to read` : ""}
        </dd>
      </div>
      <div>
        <dt>Paired</dt>
        <dd>
          Confident readings ({finiteNumber(data.confidence_min) ?? 0.7} or more) with the node's air gap within{" "}
          {pairS} s of the photo
        </dd>
      </div>
      <div>
        <dt>Not paired</dt>
        <dd>
          {count(photos.refused).toLocaleString()} refused (night, float out of view) ·{" "}
          {count(photos.unmatched).toLocaleString()} with no node reading near them
        </dd>
      </div>
    </dl>
  );
}

// Accuracy against the ruler camera: the server reads the ruler camera's
// photos and pairs every confident reading with the node's air gap near it
// (GET lab/validation/); each estimator is compared through its own fitted
// mapping. Ours is estimator v2 since it became the default; the previous
// estimator (v1) stays beside it.
export default function LabAccuracy({ node, controller, refresh = 0 }) {
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [view, setView] = useState("level");
  const { data, error, loading } = useLabValidation({ node, controller, days }, refresh, { every: REFRESH_TICKS });
  // While another window loads, the last one stays up, dimmed (not the
  // previous node's).
  const held = useRef(null);
  const scope = `${node || ""}|${controller || ""}`;
  if (data) held.current = { scope, data, days };
  const last = held.current?.scope === scope ? held.current : null;
  const shown = data || (node && loading ? last?.data : null) || null;
  const shownDays = data ? days : last?.days ?? days;
  const rows = useMemo(() => accuracyRows(shown), [shown]);
  const paired = finiteNumber(shown?.photos?.paired) ?? (shown?.pairs || []).length;
  const [, , heading] = VIEWS.find(([key]) => key === view);
  return (
    <section className="field-panel field-lab-accuracy" aria-labelledby="lab-accuracy-title">
      <div className="field-panel-head">
        <div>
          <h2 id="lab-accuracy-title">Accuracy against the ruler camera</h2>
          <p>{shown ? lead(shown) : "Each estimator's error against the ruler camera's photos, read by the server."}</p>
        </div>
        <Segment label="Over the last" options={ACCURACY_DAYS} value={days} onChange={setDays} />
      </div>
      <div
        className={`field-panel-body field-lab${shown && shown !== data ? " is-stale" : ""}`}
        aria-busy={!!node && loading}
      >
        {!node && <p className="field-caption">Choose the field node first.</p>}
        {error && (
          <div className="field-alert" role="alert" style={{ marginBottom: 0 }}>
            {error}
          </div>
        )}
        {node && !shown && !error && loading && (
          <p className="field-caption">Measuring each estimator against the ruler…</p>
        )}
        {shown &&
          (paired > 0 && rows.length ? (
            <>
              <div className="field-lab-accuracy-table-wrap">
                <AccuracyTable rows={rows} />
              </div>
              <div className="field-lab-accuracy-chart">
                <div className="field-lab-chart-head">
                  <h3 className="field-radar-heading">{heading}</h3>
                  <div className="field-segment" role="group" aria-label="Chart">
                    {VIEWS.map(([key, text]) => (
                      <button
                        key={key}
                        type="button"
                        className={view === key ? "active" : ""}
                        aria-pressed={view === key}
                        onClick={() => setView(key)}
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                </div>
                <ErrorChart rows={rows} pairs={shown.pairs} x={view} />
              </div>
            </>
          ) : (
            <p className="field-caption" style={{ margin: 0 }}>
              No confident ruler reading of the last {daysText(shownDays)} has a radar capture within{" "}
              {finiteNumber(shown.pair_s) ?? 30} s of its photo. The comparison needs the ruler camera photographing
              the tank while the node captures, as it does through a lab run.
            </p>
          ))}
        {shown && (
          <More label="Mapping and pairing">
            {paired > 0 && rows.length > 0 && (
              <>
                <MappingTable rows={rows} />
                <p className="field-caption" style={{ margin: 0 }}>
                  Mapping: level = constant − air gap / 10, fitted to the ruler for each estimator (the constant, a
                  median); the air gap per level is how many mm it moves per mm of level on a free straight line
                  (1.000 when it follows the water).
                </p>
              </>
            )}
            <Facts data={shown} />
          </More>
        )}
      </div>
    </section>
  );
}
